import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, LogOut, ArrowRight, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import { newsletterService, authService } from '../services/api';

function parseJson(value, fallback) {
  if (!value) return fallback;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function AdminNewsletter() {
  const navigate = useNavigate();
  const [newsletterId, setNewsletterId] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editingBenefitId, setEditingBenefitId] = useState(null);
  const [benefitForm, setBenefitForm] = useState({ title: '', desc: '' });

  useEffect(() => {
    const fetchNewsletter = async () => {
      try {
        const response = await newsletterService.get();
        if (response.data && response.data.id) {
          setNewsletterId(response.data.id);
          setTitle(response.data.title || '');
          setDescription(response.data.description || '');
          setBenefits(parseJson(response.data.benefits, []));
        }
      } catch (err) {
        console.error('Error fetching newsletter:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchNewsletter();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const handleSave = async () => {
    try {
      const data = { title, description, benefits: JSON.stringify(benefits) };
      if (newsletterId) {
        await newsletterService.update(newsletterId, data);
      } else {
        const response = await newsletterService.create(data);
        setNewsletterId(response.data.id);
      }
      setEditing(false);
      setError('');
    } catch (err) {
      setError('خطا در ذخیره خبرنامه');
    }
  };

  const handleAddBenefit = () => {
    if (!benefitForm.title.trim()) return;
    if (editingBenefitId !== null) {
      setBenefits(benefits.map((b, i) => i === editingBenefitId ? benefitForm : b));
    } else {
      setBenefits([...benefits, benefitForm]);
    }
    setBenefitForm({ title: '', desc: '' });
    setEditingBenefitId(null);
  };

  const handleEditBenefit = (index) => {
    setBenefitForm(benefits[index]);
    setEditingBenefitId(index);
  };

  const handleDeleteBenefit = (index) => {
    setBenefits(benefits.filter((_, i) => i !== index));
  };

  const handleCancelBenefit = () => {
    setBenefitForm({ title: '', desc: '' });
    setEditingBenefitId(null);
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">در حال بارگذاری...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">مدیریت خبرنامه</h1>
              <p className="text-xl text-blue-100">ویرایش محتوای صفحه اشتراک خبرنامه</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/admin/articles')} className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg font-semibold transition">
                <ArrowRight size={18} />
                بازگشت
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition">
                <LogOut size={18} />
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="text-blue-700" size={28} />
            <h2 className="text-3xl font-bold text-gray-900">تنظیمات خبرنامه</h2>
          </div>

          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">توضیحات</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows="4" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div className="flex gap-4">
                <button onClick={handleSave} className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition">ذخیره</button>
                <button onClick={() => setEditing(false)} className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition">انصراف</button>
              </div>
            </div>
          ) : (
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{title || 'بدون عنوان'}</h3>
              <p className="text-gray-700 mb-6">{description || 'بدون توضیحات'}</p>
              <button onClick={() => setEditing(true)} className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition">
                <Edit2 size={18} />
                ویرایش
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">مزایای اشتراک</h2>
          <div className="flex flex-wrap gap-4 mb-6">
            <input type="text" value={benefitForm.title} onChange={e => setBenefitForm({ ...benefitForm, title: e.target.value })} placeholder="عنوان" className="flex-1 min-w-[200px] px-4 py-3 border border-gray-300 rounded-lg" />
            <input type="text" value={benefitForm.desc} onChange={e => setBenefitForm({ ...benefitForm, desc: e.target.value })} placeholder="توضیح" className="flex-[2] min-w-[300px] px-4 py-3 border border-gray-300 rounded-lg" />
            <button onClick={handleAddBenefit} className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition">
              <Plus size={18} />
              {editingBenefitId !== null ? 'به‌روزرسانی' : 'افزودن'}
            </button>
            {editingBenefitId !== null && (
              <button onClick={handleCancelBenefit} className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition">انصراف</button>
            )}
          </div>

          <div className="space-y-4">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-bold text-gray-900">{benefit.title}</h4>
                  <p className="text-gray-600 text-sm">{benefit.desc}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEditBenefit(idx)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"><Edit2 size={16} /></button>
                  <button onClick={() => handleDeleteBenefit(idx)} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminNewsletter;
